<script lang="ts">
import "../app.css";
import HamburgerMenu from "../components/hamburger-menu.svelte";
import ArrowUpward from "../components/icons/arrow-upward.svelte";
import DesktopMenu from "../components/desktop-menu.svelte";
import type { Link } from "$lib/types";
import { page } from "$app/stores";
import { derived } from "svelte/store";
import { onMount } from "svelte";
import SendSvg from "../components/icons/send-svg.svelte";
import ArrowRight from "../components/icons/arrow-right.svelte";
import PriceRevisionBanner from "../components/price-revision-banner.svelte";
import type { LayoutData } from "./$types";
import { resolvePageSeo } from "$lib/seo/resolve-page-seo";
import { buildOrganizationJsonLd } from "$lib/seo/structured-data";
import JsonLd from "../components/json-ld.svelte";

export let data: LayoutData;

$: seo = resolvePageSeo($page);
const organizationJsonLd = buildOrganizationJsonLd();

let isDesktop = false;

$: venueLink =
	data.studioCount >= 2
		? { text: "スタジオ一覧", href: "/studios/" }
		: { text: "受講会場", href: "/access/" };

onMount(() => {
	isDesktop = window.innerWidth > 640;
});

/** 現在のページが問い合わせフォームや申込フォームの場合、ナビゲーション系のリンクを非表示にする */
let hideNavigation = false;
/** 画面下部にコンバージョンリンクを固定するページの場合、画面上部に戻るフローティングボタンを非表示にする*/
let showsBottomConversion = false;
/** 料金改定バナーを表示するページ */
let showsPriceRevisionBanner = false;

// 現在のパスをリアクティブに監視
const currentPath = derived(page, ($page) => $page.url.pathname);
currentPath.subscribe((path) => {
	hideNavigation = /(\/entry(\/|$)|\/contact(\/|$))/.test(path) && !/\/thanks\//.test(path);
	showsBottomConversion = /^\/(courses|prices)\/[^/]+\/$|^\/schedule\/$/.test(
		path,
	);
	showsPriceRevisionBanner =
		/^\/(prices(\/(single|set))?|schedule)\/$/.test(path);
});

function scrollToTop() {
	window.scrollTo({
		top: 0,
		behavior: "smooth", // スムーズなスクロール動作
	});
}

const footerItems: Link[] = [
	{ text: "サイトマップ", href: "/sitemap/" },
	{ text: "申込規約", href: "/terms/" },
	{ text: "特定商取引法に基づく表記", href: "/tradelaw/" },
];
</script>

<svelte:head>
  <title>{seo.title}</title>
  <meta name="description" content={seo.description} />
  <link rel="canonical" href={seo.canonical} />
  {#if seo.noindex}
    <meta name="robots" content="noindex, nofollow" />
  {/if}
</svelte:head>

<JsonLd data={organizationJsonLd} />

<header class="fixed w-screen h-[72px] bg-white border-b border-solid border-borderGray text-textBlack z-10">
  <div class="flex items-center justify-between h-full px-4 sm:px-10">
    <a
      href="/"
      class=""
    >
      <p class="font-semibold tracking-wider flex flex-col items-start justify-center">
        <small class="pl-1 text-textBalancedBodyGray text-xs">
          ピラティスインストラクター養成講座
        </small>
        <img
          src="/balanced-body-logo.webp"
          alt="Balanced Bodyのロゴ"
          class="h-12 aspect-[1000/211] -mt-1 -ml-1"
        >
      </p>
    </a>

    {#if !hideNavigation}
      <div class="sm:hidden">
        <HamburgerMenu {venueLink} />
      </div>
      
      <div class="hidden sm:flex sm:items-center sm:gap-x-4">
        <DesktopMenu {venueLink} />
        <a href="/contact/" target="_blank" class="border border-solid border-action rounded-md px-4 py-2 text-sm text-action hover:opacity-70">
          質問や無料相談はこちら
        </a>
        <a href="/entry/" target="_blank" class="border border-solid border-action rounded-md px-4 py-2 text-sm text-white bg-action hover:opacity-70">
          受講を申し込む
        </a>
      </div>
    {/if}
  </div>
</header>

{#if showsPriceRevisionBanner}
  <div class="fixed top-[72px] left-0 right-0 z-[9] w-full">
    <PriceRevisionBanner />
  </div>
{/if}

<main
  class="min-h-[calc(100vh_-_350px)] sm:min-h-[calc(100vh_-_256px)] bg-bgGray"
  class:pt-[72px]={!showsPriceRevisionBanner}
  class:pt-36={showsPriceRevisionBanner}
>
  <slot />
</main>

{#if !showsBottomConversion || isDesktop}
  <button
    id="fab-scroll-top"
    title="Scroll Back To Top Button"
    class="fixed bottom-6 right-6 w-16 h-16 rounded-full border border-solid border-action bg-white flex items-center justify-center"
    on:click={scrollToTop}
  >
    <ArrowUpward
      color="#4352F5"
      size={20}
    />
  </button>
{/if}

<!-- スマホ版の特定ページのみ、画面下部に受講申し込みボタンを固定で設置 -->
{#if showsBottomConversion && !isDesktop}
  <div class="fixed bottom-0 left-0 right-0 w-screen border-t border-solid border-borderGray bg-white py-4 px-6">
    <div class="flex items-center justify-between">
      <p class="text-sm mr-4">
        対面でもオンラインでも受講できます
      </p>
      <!-- 申し込みボタン -->
      <a
        href="/entry/"
        target="_blank"
        class="w-[260px] pl-3 pr-1 py-2 flex items-center justify-between rounded-lg bg-primary text-white border-primary border-solid border-2 hover:opacity-70"
      >
        <div class="flex items-center gap-x-2">
          <div class="font-bold text-base">
            受講を申し込む
          </div>
        </div>
        <ArrowRight color="#FFF" />
      </a>
    </div>
  </div>
{/if}

<footer
  data-bottom-space={showsBottomConversion && !isDesktop}
  class="bg-bgGray py-6 data-[bottom-space=true]:pb-32 sm:pt-16 sm:pb-16"
>
  <div class="px-6 sm:px-0 sm:w-[1132px] sm:mx-auto">
    <div class="flex flex-col sm:flex-row sm:justify-between">
      <!-- 会社情報ブロック -->
      <div class="sm:max-w-[480px]">
        <h2 class="text-lg sm:text-xl mb-2 hover:text-action">
          <a href="https://interdigitate.co.jp/" target="_blank">
            株式会社インターディジテイト
          </a>
        </h2>
        <p class="text-sm">
          ©︎ Interdigitate Inc.
        </p>
        <div class="mt-8">
          <p class="text-sm leading-relaxed mb-4">
            私どもは、Balanced Body米国本部から直接認定を受けた育成センター<br class="hidden sm:block">（Authorized Training Center）です
          </p>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-[300px] sm:max-w-none">
            {#each [1, 2, 3, 4] as number}
              <img src={`/atc${number}.webp`} loading="lazy" alt="Balanced Body認定育成センター">
            {/each}
          </div>
        </div>
      </div>
      <!-- リンクブロック -->
      <div class="grid grid-cols-1 grid-rows-[120px_1fr] sm:grid-rows-[88px_1fr_1fr]">
        <div class="hidden sm:block w-full" />
        <div class="flex items-center gap-6 mt-8 sm:my-0">
          <a href="https://www.instagram.com/v8_instructor_training/" target="_blank" class="">
            <svg class="bg-textGray rounded-full border-textGray border-solid border" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" width="44" height="44" role="img" aria-label="Instagram" style="flex-shrink:0">
              <path d="M16 18.8C14.5 18.8 13.2 17.6 13.2 16C13.2 14.5 14.4 13.2 16 13.2C17.5 13.2 18.8 14.4 18.8 16C18.8 17.5 17.5 18.8 16 18.8Z" fill="white"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M19.4 9.2H12.6C11.8 9.3 11.4 9.4 11.1 9.5C10.7 9.6 10.4 9.8 10.1 10.1C9.86261 10.3374 9.75045 10.5748 9.61489 10.8617C9.57916 10.9373 9.5417 11.0166 9.5 11.1C9.48453 11.1464 9.46667 11.1952 9.44752 11.2475C9.34291 11.5333 9.2 11.9238 9.2 12.6V19.4C9.3 20.2 9.4 20.6 9.5 20.9C9.6 21.3 9.8 21.6 10.1 21.9C10.3374 22.1374 10.5748 22.2495 10.8617 22.3851C10.9374 22.4209 11.0165 22.4583 11.1 22.5C11.1464 22.5155 11.1952 22.5333 11.2475 22.5525C11.5333 22.6571 11.9238 22.8 12.6 22.8H19.4C20.2 22.7 20.6 22.6 20.9 22.5C21.3 22.4 21.6 22.2 21.9 21.9C22.1374 21.6626 22.2495 21.4252 22.3851 21.1383C22.4209 21.0626 22.4583 20.9835 22.5 20.9C22.5155 20.8536 22.5333 20.8048 22.5525 20.7525C22.6571 20.4667 22.8 20.0762 22.8 19.4V12.6C22.7 11.8 22.6 11.4 22.5 11.1C22.4 10.7 22.2 10.4 21.9 10.1C21.6626 9.86261 21.4252 9.75045 21.1383 9.61488C21.0627 9.57918 20.9833 9.54167 20.9 9.5C20.8536 9.48453 20.8048 9.46666 20.7525 9.44752C20.4667 9.3429 20.0762 9.2 19.4 9.2ZM16 11.7C13.6 11.7 11.7 13.6 11.7 16C11.7 18.4 13.6 20.3 16 20.3C18.4 20.3 20.3 18.4 20.3 16C20.3 13.6 18.4 11.7 16 11.7ZM21.4 11.6C21.4 12.1523 20.9523 12.6 20.4 12.6C19.8477 12.6 19.4 12.1523 19.4 11.6C19.4 11.0477 19.8477 10.6 20.4 10.6C20.9523 10.6 21.4 11.0477 21.4 11.6Z" fill="white"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16ZM12.6 7.7H19.4C20.3 7.8 20.9 7.9 21.4 8.1C22 8.4 22.4 8.6 22.9 9.1C23.4 9.6 23.7 10.1 23.9 10.6C24.1 11.1 24.3 11.7 24.3 12.6V19.4C24.2 20.3 24.1 20.9 23.9 21.4C23.6 22 23.4 22.4 22.9 22.9C22.4 23.4 21.9 23.7 21.4 23.9C20.9 24.1 20.3 24.3 19.4 24.3H12.6C11.7 24.2 11.1 24.1 10.6 23.9C10 23.6 9.6 23.4 9.1 22.9C8.6 22.4 8.3 21.9 8.1 21.4C7.9 20.9 7.7 20.3 7.7 19.4V12.6C7.8 11.7 7.9 11.1 8.1 10.6C8.4 10 8.6 9.6 9.1 9.1C9.6 8.6 10.1 8.3 10.6 8.1C11.1 7.9 11.7 7.7 12.6 7.7Z" fill="white"></path>
            </svg>
          </a>
          <a href="https://www.youtube.com/watch?v=XhlvH7zv8Cc&t=1s" target="_blank">
            <svg class="bg-textGray rounded-full border-textGray border-solid border" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" width="44" height="44" role="img" aria-label="Youtube" style="flex-shrink:0">
              <path d="M18.6 16L14.4 13.6V18.4L18.6 16Z" fill="white"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16ZM22.2 10.7C22.9 10.9 23.4 11.4 23.6 12.1C24 13.4 24 16 24 16C24 16 24 18.6 23.7 19.9C23.5 20.6 23 21.1 22.3 21.3C21 21.6 16 21.6 16 21.6C16 21.6 10.9 21.6 9.7 21.3C9 21.1 8.5 20.6 8.3 19.9C8 18.6 8 16 8 16C8 16 8 13.4 8.2 12.1C8.4 11.4 8.90001 10.9 9.60001 10.7C10.9 10.4 15.9 10.4 15.9 10.4C15.9 10.4 21 10.4 22.2 10.7Z" fill="white"></path>
            </svg>
          </a>
        </div>
        <div class="flex items-end">
          <ul class="flex flex-col gap-y-2 sm:items-center sm:flex-row sm:gap-x-6">
            <li>
              <a href="https://v8pilatesacademy.com/" target="_blank" class="underline">
                <p class="text-sm">V8ピラティスアカデミー</p>
              </a>
            </li>
            <li>
              <a href="https://interdigitate.co.jp/" target="_blank" class="underline">
                <p class="text-sm">会社情報</p>
              </a>
            </li>
            {#each footerItems as { text, href }}
              <li>
                <a href={href} class="underline">
                  <p class="text-sm">{text}</p>
                </a>
              </li>
            {/each}
          </ul>
        </div>
      </div>
    </div>
  </div>
</footer>